import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Close, ArrowBack, Email } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import PasswordRequirements from '../PasswordRequirements';
import { validatePassword } from '../../utils/passwordValidation';
import ResendConfirmationModal from '../ResendConfirmationModal';

// Google Icon component
const GoogleIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.8446H13.8436C13.635 11.9696 13.0009 12.9232 12.0477 13.5616V15.8186H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z" fill="#4285F4"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8186L12.0477 13.5616C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z" fill="#FBBC04"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
  </svg>
);

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'signin' | 'signup';
  onModeChange: (mode: 'signin' | 'signup') => void;
  externalError?: string | null;
  showForgotPassword?: boolean;
}

const AuthModal: React.FC<AuthModalProps> = ({ 
  open, 
  onClose, 
  mode, 
  onModeChange, 
  externalError, 
  showForgotPassword = false 
}) => {
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(showForgotPassword);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);

  // Check if signup form is valid
  const isSignupValid = () => {
    if (mode !== 'signup') return true;
    if (!email || !password || !confirmPassword) return false;
    const validation = validatePassword(password, confirmPassword);
    return validation.isValid;
  };

  // Set external error when modal opens
  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  // Initialize forgot password mode if requested
  useEffect(() => {
    setShowResetPassword(showForgotPassword);
  }, [showForgotPassword]);

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setMessage('');
    setShowResetPassword(false);
    setSignupSuccess(false);
    setRememberMe(true);
    onClose();
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: 'signin' | 'signup') => {
    setError('');
    setMessage('');
    setShowResetPassword(false);
    onModeChange(newValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (showResetPassword) {
        // Handle password reset
        if (!email) {
          setError('Please enter your email address');
          return;
        }

        const { error } = await resetPassword(email);
        if (error) {
          setError(error);
        } else {
          setMessage('Password reset email sent! Check your inbox.');
          setShowResetPassword(false);
        }
      } else if (mode === 'signup') {
        // Handle sign up
        if (!email || !password || !confirmPassword) {
          setError('Please fill in all fields');
          return;
        }

        const passwordValidation = validatePassword(password, confirmPassword);
        if (!passwordValidation.isValid) {
          const unmetRequirements = passwordValidation.requirements
            .filter(req => !req.met)
            .map(req => req.label);
          setError(`Password must meet the following requirements: ${unmetRequirements.join(', ')}`);
          return;
        }

        const { error, needsConfirmation, confirmationEmail } = await signUp(email, password);
        if (error) {
          // Check if this is an unconfirmed account error
          if (error === 'UNCONFIRMED_ACCOUNT' || error.includes('already exists but is not yet activated')) {
            // Show resend confirmation modal instead of just error
            setShowResendConfirmation(true);
            setError(''); // Clear the error since we're showing the modal
          } else {
            setError(error);
          }
        } else if (needsConfirmation && confirmationEmail) {
          // Show resend confirmation modal for existing unconfirmed account
          setShowResendConfirmation(true);
          setError(''); // Clear any errors
        } else {
          setMessage('Account created successfully! Please check your email to confirm your account.');
          setSignupSuccess(true);
          // Keep the form open but disable all fields
        }
      } else {
        // Handle sign in
        if (!email || !password) {
          setError('Please fill in all fields');
          return;
        }

        const { error } = await signIn(email, password, rememberMe);
        if (error) {
          setError(error);
        } else {
          handleClose();
        }
      }
    } catch (err) {
      console.error('AuthModal error:', err);
      // Only show "unexpected error" for actual network errors or unexpected exceptions
      // Auth errors should already be handled above
      setError('Network error occurred. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = () => {
    setShowResendConfirmation(true);
    setError('');
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      disableRestoreFocus
      maxWidth={signupSuccess ? "xs" : "xs"} 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          mx: 2, // Add horizontal margin for mobile
          width: '100%',
          maxWidth: signupSuccess ? '320px' : '380px' // Constrain max width
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div">
          {showResetPassword ? 'Reset Password' : (mode === 'signin' ? 'Sign In' : 'Sign Up')}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {!showResetPassword && !signupSuccess && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs 
            value={mode} 
            onChange={handleTabChange} 
            aria-label="auth tabs"
          >
            <Tab label="Sign In" value="signin" disabled={signupSuccess} />
            <Tab label="Sign Up" value="signup" disabled={signupSuccess} />
          </Tabs>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: signupSuccess ? 0 : 2, pb: signupSuccess ? 0 : 0.5, px: 3 }}>
          {signupSuccess ? (
            // Success state - clean minimal UI
            <Box sx={{ 
              textAlign: 'center', 
              py: 3, // Reduced from 4 for more compact height
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <Email sx={{ 
                fontSize: 56, // Reduced from 64 for more compact height
                color: '#2e7d32', // Dark green
                mb: 2
              }} />
              <Typography variant="h6" color="#2e7d32" sx={{ mb: 2, fontWeight: 600 }}>
                Account Created Successfully!
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ maxWidth: 400, fontWeight: 500, mb: 1.5 }}>
                ⚠️ <strong>Account Confirmation Required</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, lineHeight: 1.6, mb: 2 }}>
                We've sent a confirmation email to <strong>{email}</strong>. 
                You must click the verification link in your email to activate your account before you can sign in.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, fontStyle: 'italic', opacity: 0.8 }}>
                💡 Don't forget to check your spam folder if you don't see the email.
              </Typography>
            </Box>
          ) : (
            <>
              {error && (
                <>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                    {error.toLowerCase().includes('invalid login credentials') && mode === 'signin' && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Account not confirmed yet?{' '}
                          <Button
                            variant="text"
                            size="small"
                            onClick={handleResendConfirmation}
                            sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                          >
                            Resend confirmation email
                          </Button>
                        </Typography>
                      </Box>
                    )}
                  </Alert>
                </>
              )}

              {message && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {message}
                </Alert>
              )}

          {showResetPassword && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton
                onClick={() => setShowResetPassword(false)}
                size="small"
                sx={{ 
                  mr: 1,
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                Back to sign in
              </Typography>
            </Box>
          )}

          {showResetPassword && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Password Reset Instructions:</strong><br />
                • We'll send a secure reset link to your email address<br />
                • The link is valid for 24 hours<br />
                • Each link can only be used once for security<br />
                • Check your spam folder if you don't see the email
              </Typography>
            </Alert>
          )}

              <TextField
                margin="dense"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading || signupSuccess}
                size="small"
                sx={{ mb: 1 }}
              />

              {!showResetPassword && (
                <>
                  {/* Divider between email and password fields */}
                  <Divider>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Password
                    </Typography>
                  </Divider>

                  <TextField
                    margin="dense"
                    label="Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading || signupSuccess}
                    size="small"
                    sx={{ mb: 1 }}
                  />

                  {mode === 'signup' && (
                    <>
                      <TextField
                        margin="dense"
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={loading || signupSuccess}
                        size="small"
                        error={password.length > 0 && (confirmPassword.length === 0 || password !== confirmPassword)}
                        helperText={
                          password.length > 0 && confirmPassword.length === 0 
                            ? "Please confirm your password"
                            : confirmPassword.length > 0 && password !== confirmPassword 
                              ? "Passwords do not match" 
                              : ""
                        }
                        sx={{ mb: 0.5 }}
                      />
                      
                      {/* Show password requirements for sign up */}
                      <PasswordRequirements password={password} confirmPassword={confirmPassword} />
                    </>
                  )}
                </>
              )}          {/* Remember me checkbox - only show for sign in mode */}
          {!showResetPassword && mode === 'signin' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading || signupSuccess}
                  color="primary"
                />
              }
              label="Remember me"
              sx={{ mb: 1, alignSelf: 'flex-start' }}
            />
          )}

          {!showResetPassword && (
            <>
              <Box sx={{ my: 1.5 }}> {/* Reduced from my: 2 */}
                <Divider> {/* Removed margin */}
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    or
                  </Typography>
                </Divider>
                
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  onClick={() => signInWithGoogle(true)} // Always use rememberMe: true for OAuth
                  disabled={loading || signupSuccess}
                  startIcon={<GoogleIcon />}
                  sx={{
                    mb: 0.5, /* Reduced from mb: 1 */
                    textTransform: 'none',
                    borderColor: '#dadce0',
                    color: '#3c4043',
                    py: 0.75, /* Make button more compact */
                    '&:hover': {
                      borderColor: '#dadce0',
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                >
                  {mode === 'signin' ? 'Sign in with Google' : 'Sign up with Google'}
                </Button>
              </Box>
            </>
          )}

          {mode === 'signin' && !showResetPassword && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowResetPassword(true)}
              >
                Forgot password?
              </Button>
            </Box>
          )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          {!signupSuccess && (
            <>
              <Button onClick={handleClose} disabled={loading} size="small">
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading || (mode === 'signup' && !isSignupValid())}
                size="small"
                sx={{ minWidth: 90, py: 0.75 }}
              >
                {loading ? 'Loading...' : (
                  showResetPassword ? 'Send Reset Email' : (
                    mode === 'signin' ? 'Sign In' : 'Sign Up'
                  )
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </form>

      <ResendConfirmationModal
        open={showResendConfirmation}
        onClose={() => {
          setShowResendConfirmation(false);
        }}
        initialEmail={email}
        reason="unconfirmed"
      />
    </Dialog>
  );
};

export default AuthModal;
